import { z } from 'zod';

export const patientAllergyDetailDtoSchema = z.object({
  tenantId: z.string().nullable(),
  allergenName: z.string().nullable(),
  reaction: z.string().nullable(),
  reactionNote: z.string().nullable()
});
export type PatientAllergyDetailDto = z.infer<typeof patientAllergyDetailDtoSchema>;

export const encounterOrderDtoSchema = z.object({
  orderCategory: z.string().nullable(),
  orderCode: z.string().nullable(),
  orderName: z.string().nullable(),
  orderStartDatetime: z.string().nullable(),
  orderEndDatetime: z.string().nullable(),
  authoredDatetime: z.string().nullable(),
  scheduleDatetime: z.string().nullable(),
  dosageQty: z.number().nullable(),
  dosageUnit: z.string().nullable(),
  routeCode: z.string().nullable(),
  freqCode: z.string().nullable(),
  timingCode: z.string().nullable(),
  days: z.number().nullable(),
  instructionMemo: z.string().nullable(),
  prnFlag: z.boolean().nullable(),
  injectionFlag: z.boolean().nullable(),
  emergencyFlag: z.boolean().nullable(),
  stopFlag: z.boolean().nullable(),
  reportId: z.string().nullable(),
  reportType: z.string().nullable(),
  boundMedications: z.unknown().nullable()
});
export type EncounterOrderDto = z.infer<typeof encounterOrderDtoSchema>;

export const encounterDiagnosisDtoSchema = z.object({
  icdCode: z.string().nullable(),
  diagnosisNameEn: z.string().nullable(),
  diagnosisNameZh: z.string().nullable()
});
export type EncounterDiagnosisDto = z.infer<typeof encounterDiagnosisDtoSchema>;

export const encounterSurgeryDtoSchema = z.object({
  reportContent: z.string().nullable()
});
export type EncounterSurgeryDto = z.infer<typeof encounterSurgeryDtoSchema>;

export const encounterDetailDtoSchema = z.object({
  subjective: z.string().nullable(),
  objective: z.string().nullable()
});
export type EncounterDetailDto = z.infer<typeof encounterDetailDtoSchema>;

export const patientAllergyDtoSchema = z.object({
  allergyDescription: z.string().nullable(),
  patientAllergyDetails: z.array(patientAllergyDetailDtoSchema).nullable()
});
export type PatientAllergyDto = z.infer<typeof patientAllergyDtoSchema>;

export const patientConditionDtoSchema = z.object({
  personalHistory: z.string().nullable(),
  familyHistory: z.string().nullable()
});
export type PatientConditionDto = z.infer<typeof patientConditionDtoSchema>;

export const medicationRequestDtoSchema = z.object({
  orderCode: z.string().nullable(),
  orderName: z.string().nullable(),
  dosageQty: z.number().nullable(),
  dosageUnit: z.string().nullable(),
  freqCode: z.string().nullable(),
  emergencyFlag: z.boolean().nullable(),
  prnFlag: z.boolean().nullable(),
  injectionFlag: z.boolean().nullable(),
  authoredDatetime: z.string().nullable()
});
export type MedicationRequestDto = z.infer<typeof medicationRequestDtoSchema>;

export const tprDataDtoSchema = z.object({
  effectiveDatetime: z.string().nullable(),
  valueQuantity: z.number().nullable(),
  valueUnit: z.string().nullable(),
  valueString: z.string().nullable(),
  shiftPeriod: z.string().nullable(),
  note: z.string().nullable()
});
export type TprDataDto = z.infer<typeof tprDataDtoSchema>;

export const labReportDetailDtoSchema = z.object({
  itemCode: z.string().nullable(),
  itemNameEn: z.string().nullable(),
  itemNameZh: z.string().nullable(),
  valueUnit: z.string().nullable(),
  valueQuantity: z.number().nullable(),
  resultStatus: z.string().nullable(),
  valueString: z.string().nullable(),
  normalRangeLow: z.number().nullable(),
  normalRangeHigh: z.number().nullable(),
  normalText: z.string().nullable(),
  effectiveDatetime: z.string().nullable(),
  note: z.string().nullable(),
  resultStatusI18n: z.string().nullable()
});
export type LabReportDetailDto = z.infer<typeof labReportDetailDtoSchema>;

export const debtDtoSchema = z.object({
  tenantId: z.string().nullable(),
  debtAmount: z.number().nullable()
});
export type DebtDto = z.infer<typeof debtDtoSchema>;

export const bloodTypeDtoSchema = z.object({
  tenantId: z.string().nullable(),
  bloodType: z.string().nullable()
});
export type BloodTypeDto = z.infer<typeof bloodTypeDtoSchema>;

export const familyHistoryDtoSchema = z.object({
  tenantId: z.string().nullable(),
  familyHistoryDescription: z.string().nullable()
});
export type FamilyHistoryDto = z.infer<typeof familyHistoryDtoSchema>;

export const personalHistoryDtoSchema = z.object({
  tenantId: z.string().nullable(),
  personalHistoryDescription: z.string().nullable()
});
export type PersonalHistoryDto = z.infer<typeof personalHistoryDtoSchema>;

export const cancerScreenDtoSchema = z.object({
  tenantId: z.string().nullable(),
  eligibleOralCancerI18n: z.string().nullable(),
  eligibleColorectalCancerI18n: z.string().nullable(),
  eligibleCervicalCancerI18n: z.string().nullable(),
  eligibleBreastCancerI18n: z.string().nullable(),
  eligibleLungCancerI18n: z.string().nullable(),
  eligibleHpvCancerI18n: z.string().nullable(),
  oralScreenDate: z.string().nullable(),
  oralScreenResult: z.string().nullable(),
  oralScreenHospital: z.string().nullable(),
  colorectalScreenDate: z.string().nullable(),
  colorectalScreenResult: z.string().nullable(),
  colorectalScreenHospital: z.string().nullable(),
  cervicalScreenDate: z.string().nullable(),
  cervicalScreenResult: z.string().nullable(),
  cervicalScreenHospital: z.string().nullable(),
  breastScreenDate: z.string().nullable(),
  breastScreenResult: z.string().nullable(),
  breastScreenHospital: z.string().nullable(),
  lungScreenDate: z.string().nullable(),
  lungScreenResult: z.string().nullable(),
  lungScreenHospital: z.string().nullable(),
  hpvScreenDate: z.string().nullable(),
  hpvScreenResult: z.string().nullable(),
  hpvScreenHospital: z.string().nullable(),
  oralScreenResultI18n: z.string().nullable(),
  colorectalScreenResultI18n: z.string().nullable(),
  cervicalScreenResultI18n: z.string().nullable(),
  breastScreenResultI18n: z.string().nullable(),
  lungScreenResultI18n: z.string().nullable(),
  hpvScreenResultI18n: z.string().nullable()
});
export type CancerScreenDto = z.infer<typeof cancerScreenDtoSchema>;

export const ptCatastrophicIllnessDtoSchema = z.object({
  tenantId: z.string().nullable(),
  icdCode: z.string().nullable(),
  diagnosisDesc: z.string().nullable(),
  cardNumber: z.string().nullable(),
  effDateStart: z.string().nullable(),
  effDateEnd: z.string().nullable()
});
export type PtCatastrophicIllnessDto = z.infer<typeof ptCatastrophicIllnessDtoSchema>;

export const recentVisitDtoSchema = z.object({
  encounterId: z.string().nullable(),
  recentVisitType: z.enum(["IP", "OP", "ER", "PE", "SURGERY"]).nullable(),
  recentVisitTypeDesc: z.string().nullable(),
  visitStartDatetime: z.string().nullable(),
  icdCode: z.string().nullable(),
  diagnosisDesc: z.string().nullable(),
  surgeryNameEn: z.string().nullable(),
  surgeryNameZh: z.string().nullable()
});
export type RecentVisitDto = z.infer<typeof recentVisitDtoSchema>;

export const allergyDescriptionDtoSchema = z.object({
  tenantId: z.string().nullable(),
  allergyDescription: z.string().nullable()
});
export type AllergyDescriptionDto = z.infer<typeof allergyDescriptionDtoSchema>;

export const queryPatientObservationResSchema = z.object({
  tenantId: z.string().nullable(),
  effectiveDatetime: z.string().nullable(),
  valueQuantity: z.number().nullable(),
  valueUnit: z.string().nullable(),
  abnormalStatus: z.string().nullable()
});
export type QueryPatientObservationRes = z.infer<typeof queryPatientObservationResSchema>;

export const queryAdmissionNoteResSchema = z.object({
  reportContentType: z.string().nullable(),
  reportContent: z.string().nullable()
});
export type QueryAdmissionNoteRes = z.infer<typeof queryAdmissionNoteResSchema>;

export const queryAllOutpatientDataResSchema = z.object({
  patientConditionDto: patientConditionDtoSchema.nullable(),
  patientAllergy: patientAllergyDtoSchema.nullable(),
  encounterDetail: encounterDetailDtoSchema.nullable(),
  encounterSurgeryList: z.array(encounterSurgeryDtoSchema).nullable(),
  encounterDiagnoses: z.array(encounterDiagnosisDtoSchema).nullable(),
  encounterOrders: z.array(encounterOrderDtoSchema).nullable()
});
export type QueryAllOutpatientDataRes = z.infer<typeof queryAllOutpatientDataResSchema>;

export const queryDischargeMedicationResSchema = z.object({
  orderCode: z.string().nullable(),
  orderName: z.string().nullable(),
  orderStartDatetime: z.string().nullable(),
  dosageQty: z.number().nullable(),
  dosageUnit: z.string().nullable(),
  routeCode: z.string().nullable(),
  freqCode: z.string().nullable(),
  timingCode: z.string().nullable(),
  days: z.number().nullable(),
  instructionMemo: z.string().nullable(),
  prnFlag: z.boolean().nullable(),
  boundMedications: z.array(encounterOrderDtoSchema).nullable()
});
export type QueryDischargeMedicationRes = z.infer<typeof queryDischargeMedicationResSchema>;

export const queryDischargeNoteResSchema = z.object({
  reportContentType: z.string().nullable(),
  reportContent: z.string().nullable()
});
export type QueryDischargeNoteRes = z.infer<typeof queryDischargeNoteResSchema>;

export const queryEncounterResSchema = z.object({
  id: z.string().nullable(),
  tenantId: z.string().nullable(),
  encounterType: z.string().nullable(),
  visitStartDatetime: z.string().nullable(),
  visitEndDatetime: z.string().nullable(),
  surgeryDatetimeList: z.array(z.string()).nullable(),
  doctorName: z.string().nullable(),
  divName: z.string().nullable(),
  icdCode: z.string().nullable(),
  diagnosisDesc: z.string().nullable(),
  allergenNames: z.array(z.string()).nullable(),
  roomNo: z.string().nullable(),
  bedNo: z.string().nullable(),
  seqNo: z.string().nullable(),
  bodyWeight: z.number().nullable(),
  effectiveDatetime: z.string().nullable()
});
export type QueryEncounterRes = z.infer<typeof queryEncounterResSchema>;

export const queryInpatientEncounterOrderResSchema = z.object({
  orderCategory: z.string().nullable(),
  orderCode: z.string().nullable(),
  orderName: z.string().nullable(),
  orderStartDatetime: z.string().nullable(),
  orderEndDatetime: z.string().nullable(),
  authoredDatetime: z.string().nullable(),
  scheduleDatetime: z.string().nullable(),
  dosageQty: z.number().nullable(),
  dosageUnit: z.string().nullable(),
  routeCode: z.string().nullable(),
  freqCode: z.string().nullable(),
  timingCode: z.string().nullable(),
  days: z.number().nullable(),
  instructionMemo: z.string().nullable(),
  prnFlag: z.boolean().nullable(),
  injectionFlag: z.boolean().nullable(),
  emergencyFlag: z.boolean().nullable(),
  stopFlag: z.boolean().nullable(),
  reportId: z.string().nullable(),
  reportType: z.string().nullable(),
  boundMedications: z.array(encounterOrderDtoSchema).nullable()
});
export type QueryInpatientEncounterOrderRes = z.infer<typeof queryInpatientEncounterOrderResSchema>;

export const queryInpatientFuncResSchema = z.object({
  funcs: z.array(z.enum(["admissionNote", "dischargeNote", "dischargeMedication", "surgeryReport", "labReport", "imageReport", "examRoomReport", "progressNote", "nursingNote", "inpatientEncounterOrder", "tprData"])).nullable()
});
export type QueryInpatientFuncRes = z.infer<typeof queryInpatientFuncResSchema>;

export const queryNursingNoteResSchema = z.object({
  encounterId: z.string().nullable(),
  practitionerName: z.string().nullable(),
  noteDatetime: z.string().nullable(),
  content: z.string().nullable()
});
export type QueryNursingNoteRes = z.infer<typeof queryNursingNoteResSchema>;

export const queryProgressNoteResSchema = z.object({
  effectiveDatetime: z.string().nullable(),
  subjective: z.string().nullable(),
  objective: z.string().nullable(),
  assessment: z.string().nullable(),
  plan: z.string().nullable(),
  content: z.string().nullable()
});
export type QueryProgressNoteRes = z.infer<typeof queryProgressNoteResSchema>;

export const queryTprDataResSchema = z.object({
  data: z.record(z.string(), z.record(z.string(), z.array(tprDataDtoSchema))).nullable(),
  surgeryDatetimeList: z.array(z.string()).nullable(),
  medicationList: z.array(medicationRequestDtoSchema).nullable()
});
export type QueryTprDataRes = z.infer<typeof queryTprDataResSchema>;

export const queryLabReportByIdResSchema = z.object({
  category: z.string().nullable(),
  requisitionNo: z.string().nullable(),
  reportDatetime: z.string().nullable(),
  reportDoctorName: z.string().nullable(),
  specimenCode: z.string().nullable(),
  specimenName: z.string().nullable(),
  orderDatetime: z.string().nullable(),
  orderDoctorName: z.string().nullable(),
  collectionDatetime: z.string().nullable(),
  receiveDatetime: z.string().nullable(),
  labReportDetails: z.array(labReportDetailDtoSchema).nullable(),
  categoryI18n: z.string().nullable()
});
export type QueryLabReportByIdRes = z.infer<typeof queryLabReportByIdResSchema>;

export const queryLabReportByCategoryResSchema = z.object({
  category: z.string().nullable(),
  labReportDetails: z.array(labReportDetailDtoSchema).nullable(),
  categoryI18n: z.string().nullable()
});
export type QueryLabReportByCategoryRes = z.infer<typeof queryLabReportByCategoryResSchema>;

export const queryLabReportTrendResSchema = z.object({
  trendData: z.array(labReportDetailDtoSchema).nullable()
});
export type QueryLabReportTrendRes = z.infer<typeof queryLabReportTrendResSchema>;

export const queryLabReportResSchema = z.object({
  id: z.string().nullable(),
  tenantId: z.string().nullable(),
  reportDatetime: z.string().nullable(),
  category: z.string().nullable(),
  categoryI18n: z.string().nullable()
});
export type QueryLabReportRes = z.infer<typeof queryLabReportResSchema>;

export const queryMedicationKnowledgeResSchema = z.object({
  medCode: z.string().nullable(),
  nhiCode: z.string().nullable(),
  atcCode: z.string().nullable(),
  controlLevel: z.string().nullable(),
  medNameEn: z.string().nullable(),
  medNameZh: z.string().nullable(),
  genericName: z.string().nullable(),
  pregnancyCategory: z.string().nullable(),
  pregnancyDesc: z.string().nullable(),
  ingredientDesc: z.string().nullable(),
  dosageForm: z.string().nullable(),
  dosageInstruction: z.string().nullable(),
  indication: z.string().nullable(),
  sideEffect: z.string().nullable(),
  medicalAdvice: z.string().nullable(),
  contraindication: z.string().nullable(),
  precaution: z.string().nullable(),
  appearanceDesc: z.string().nullable(),
  imageUrl: z.string().nullable(),
  imageFileId: z.string().nullable(),
  tfdaLicenseNo: z.string().nullable()
});
export type QueryMedicationKnowledgeRes = z.infer<typeof queryMedicationKnowledgeResSchema>;

export const queryPatientCardResSchema = z.object({
  patientId: z.string().nullable(),
  mrn: z.string().nullable(),
  patientName: z.string().nullable(),
  gender: z.string().nullable(),
  birthDate: z.string().nullable(),
  catastrophicIllnessFlag: z.boolean().nullable(),
  dnrFlag: z.boolean().nullable(),
  alertFlag: z.boolean().nullable(),
  allergyFlag: z.boolean().nullable(),
  isolationFlag: z.boolean().nullable(),
  fastingFlag: z.boolean().nullable(),
  icCardRemarkList: z.array(z.string()).nullable(),
  genderI18n: z.string().nullable()
});
export type QueryPatientCardRes = z.infer<typeof queryPatientCardResSchema>;

export const queryPatientDetailMultiTenantResSchema = z.object({
  patientId: z.string().nullable(),
  allergyDescriptionList: z.array(allergyDescriptionDtoSchema).nullable(),
  allergyDetailList: z.array(patientAllergyDetailDtoSchema).nullable(),
  recentVisitList: z.array(recentVisitDtoSchema).nullable(),
  catastrophicIllnessList: z.array(ptCatastrophicIllnessDtoSchema).nullable(),
  cancerScreen: cancerScreenDtoSchema.nullable(),
  personalHistoryList: z.array(personalHistoryDtoSchema).nullable(),
  familyHistoryList: z.array(familyHistoryDtoSchema).nullable(),
  maritalStatus: z.string().nullable(),
  email: z.string().nullable(),
  educationLevel: z.string().nullable(),
  incomeStatus: z.string().nullable(),
  nationality: z.string().nullable(),
  primaryLanguage: z.string().nullable(),
  religion: z.string().nullable(),
  idTypeDesc: z.string().nullable(),
  idNumber: z.string().nullable(),
  mobilePhone: z.string().nullable(),
  homePhone: z.string().nullable(),
  address: z.string().nullable(),
  occupation: z.string().nullable(),
  contactName: z.string().nullable(),
  contactRelationship: z.string().nullable(),
  contactPhone: z.string().nullable(),
  bloodType: z.array(bloodTypeDtoSchema).nullable(),
  benefitType: z.string().nullable(),
  debtAmount: z.array(debtDtoSchema).nullable(),
  maritalStatusI18n: z.string().nullable(),
  nationalityI18n: z.string().nullable(),
  primaryLanguageI18n: z.string().nullable(),
  contactRelationshipI18n: z.string().nullable()
});
export type QueryPatientDetailMultiTenantRes = z.infer<typeof queryPatientDetailMultiTenantResSchema>;

export const queryPatientDetailResSchema = z.object({
  patientId: z.string().nullable(),
  allergyDescription: z.string().nullable(),
  allergyDetailList: z.array(patientAllergyDetailDtoSchema).nullable(),
  recentVisitList: z.array(recentVisitDtoSchema).nullable(),
  catastrophicIllnessList: z.array(ptCatastrophicIllnessDtoSchema).nullable(),
  eligibleOralCancerI18n: z.string().nullable(),
  eligibleColorectalCancerI18n: z.string().nullable(),
  eligibleCervicalCancerI18n: z.string().nullable(),
  eligibleBreastCancerI18n: z.string().nullable(),
  eligibleLungCancerI18n: z.string().nullable(),
  oralScreenDate: z.string().nullable(),
  oralScreenResult: z.string().nullable(),
  oralScreenHospital: z.string().nullable(),
  colorectalScreenDate: z.string().nullable(),
  colorectalScreenResult: z.string().nullable(),
  colorectalScreenHospital: z.string().nullable(),
  cervicalScreenDate: z.string().nullable(),
  cervicalScreenResult: z.string().nullable(),
  cervicalScreenHospital: z.string().nullable(),
  breastScreenDate: z.string().nullable(),
  breastScreenResult: z.string().nullable(),
  breastScreenHospital: z.string().nullable(),
  lungScreenDate: z.string().nullable(),
  lungScreenResult: z.string().nullable(),
  lungScreenHospital: z.string().nullable(),
  personalHistory: z.string().nullable(),
  familyHistory: z.string().nullable(),
  maritalStatus: z.string().nullable(),
  email: z.string().nullable(),
  educationLevel: z.string().nullable(),
  incomeStatus: z.string().nullable(),
  nationality: z.string().nullable(),
  primaryLanguage: z.string().nullable(),
  religion: z.string().nullable(),
  idTypeDesc: z.string().nullable(),
  idNumber: z.string().nullable(),
  mobilePhone: z.string().nullable(),
  homePhone: z.string().nullable(),
  address: z.string().nullable(),
  occupation: z.string().nullable(),
  contactName: z.string().nullable(),
  contactRelationship: z.string().nullable(),
  contactPhone: z.string().nullable(),
  bloodType: z.string().nullable(),
  benefitType: z.string().nullable(),
  debtAmount: z.number().nullable(),
  oralScreenResultI18n: z.string().nullable(),
  colorectalScreenResultI18n: z.string().nullable(),
  cervicalScreenResultI18n: z.string().nullable(),
  breastScreenResultI18n: z.string().nullable(),
  lungScreenResultI18n: z.string().nullable(),
  maritalStatusI18n: z.string().nullable(),
  nationalityI18n: z.string().nullable(),
  primaryLanguageI18n: z.string().nullable(),
  contactRelationshipI18n: z.string().nullable()
});
export type QueryPatientDetailRes = z.infer<typeof queryPatientDetailResSchema>;

export const queryPatientSearchLogResSchema = z.object({
  mrn: z.string().nullable(),
  patientName: z.string().nullable(),
  startDate: z.string().nullable(),
  endDate: z.string().nullable(),
  createDatetime: z.string().nullable()
});
export type QueryPatientSearchLogRes = z.infer<typeof queryPatientSearchLogResSchema>;

export const queryPatientObservationDataResSchema = z.object({
  weights: z.array(queryPatientObservationResSchema).nullable(),
  temperatures: z.array(queryPatientObservationResSchema).nullable(),
  systolic: z.array(queryPatientObservationResSchema).nullable(),
  diastolic: z.array(queryPatientObservationResSchema).nullable(),
  heartRates: z.array(queryPatientObservationResSchema).nullable()
});
export type QueryPatientObservationDataRes = z.infer<typeof queryPatientObservationDataResSchema>;

export const queryExamRoomReportByIdResSchema = z.object({
  id: z.string().nullable(),
  requisitionNo: z.string().nullable(),
  reportContentType: z.string().nullable(),
  reportContent: z.string().nullable(),
  orderDatetime: z.string().nullable(),
  orderDoctorName: z.string().nullable(),
  reportDatetime: z.string().nullable(),
  reportPerformerName: z.string().nullable(),
  executeDateTime: z.string().nullable(),
  executeDoctorName: z.string().nullable(),
  conclusion: z.string().nullable(),
  reportName: z.string().nullable(),
  formCode: z.string().nullable(),
  examDatetime: z.string().nullable()
});
export type QueryExamRoomReportByIdRes = z.infer<typeof queryExamRoomReportByIdResSchema>;

export const queryExamReportResSchema = z.object({
  id: z.string().nullable(),
  reportPerformerName: z.string().nullable(),
  orderDatetime: z.string().nullable(),
  reportName: z.string().nullable(),
  examItemZh: z.string().nullable(),
  examItemEn: z.string().nullable(),
  tenantId: z.string().nullable(),
  examDatetime: z.string().nullable(),
  doctorName: z.string().nullable()
});
export type QueryExamReportRes = z.infer<typeof queryExamReportResSchema>;

export const queryImageReportByIdResSchema = z.object({
  id: z.string().nullable(),
  requisitionNo: z.string().nullable(),
  reportContentType: z.string().nullable(),
  reportContent: z.string().nullable(),
  orderDatetime: z.string().nullable(),
  orderDoctorName: z.string().nullable(),
  reportDatetime: z.string().nullable(),
  reportPerformerName: z.string().nullable(),
  executeDateTime: z.string().nullable(),
  executeDoctorName: z.string().nullable(),
  conclusion: z.string().nullable(),
  reportName: z.string().nullable(),
  formCode: z.string().nullable(),
  examDatetime: z.string().nullable()
});
export type QueryImageReportByIdRes = z.infer<typeof queryImageReportByIdResSchema>;

export const queryImageReportResSchema = z.object({
  id: z.string().nullable(),
  reportPerformerName: z.string().nullable(),
  orderDatetime: z.string().nullable(),
  reportName: z.string().nullable(),
  examItemZh: z.string().nullable(),
  examItemEn: z.string().nullable(),
  tenantId: z.string().nullable(),
  examDatetime: z.string().nullable(),
  doctorName: z.string().nullable()
});
export type QueryImageReportRes = z.infer<typeof queryImageReportResSchema>;

export const queryPacsConnectInfoResSchema = z.object({
  pacsUrl: z.string().nullable()
});
export type QueryPacsConnectInfoRes = z.infer<typeof queryPacsConnectInfoResSchema>;

export const queryPathologyReportContentResSchema = z.object({
  id: z.string().nullable(),
  requisitionNo: z.string().nullable(),
  reportContentType: z.string().nullable(),
  reportContent: z.string().nullable(),
  orderDatetime: z.string().nullable(),
  orderDoctorName: z.string().nullable(),
  reportDatetime: z.string().nullable(),
  reportPerformerName: z.string().nullable(),
  executeDateTime: z.string().nullable(),
  executeDoctorName: z.string().nullable(),
  encounterId: z.string().nullable(),
  patientId: z.string().nullable(),
  pathologyCode: z.string().nullable(),
  submitterName: z.string().nullable(),
  divCode: z.string().nullable(),
  divName: z.string().nullable(),
  receiveDatetime: z.string().nullable(),
  examCode: z.string().nullable(),
  examNameEn: z.string().nullable(),
  examNameZh: z.string().nullable(),
  issuedDatetime: z.string().nullable(),
  reportName: z.string().nullable(),
  submitterId: z.string().nullable()
});
export type QueryPathologyReportContentRes = z.infer<typeof queryPathologyReportContentResSchema>;

export const queryPathologyReportResSchema = z.object({
  id: z.string().nullable(),
  submitterName: z.string().nullable(),
  orderDatetime: z.string().nullable(),
  divCode: z.string().nullable(),
  divName: z.string().nullable(),
  reportName: z.string().nullable(),
  examCode: z.string().nullable(),
  examNameEn: z.string().nullable(),
  examNameZh: z.string().nullable(),
  tenantId: z.string().nullable(),
  encounterId: z.string().nullable(),
  patientId: z.string().nullable(),
  issuedDatetime: z.string().nullable(),
  submitterId: z.string().nullable()
});
export type QueryPathologyReportRes = z.infer<typeof queryPathologyReportResSchema>;

export const querySurgeryReportContentResSchema = z.object({
  id: z.string().nullable(),
  requisitionNo: z.string().nullable(),
  reportContentType: z.string().nullable(),
  reportContent: z.string().nullable(),
  orderDatetime: z.string().nullable(),
  orderDoctorName: z.string().nullable(),
  reportDatetime: z.string().nullable(),
  reportPerformerName: z.string().nullable(),
  executeDateTime: z.string().nullable(),
  executeDoctorName: z.string().nullable(),
  encounterId: z.string().nullable(),
  patientId: z.string().nullable(),
  formCode: z.string().nullable(),
  surgeryNameEn: z.string().nullable(),
  surgeryNameZh: z.string().nullable(),
  surgeryReportId: z.string().nullable()
});
export type QuerySurgeryReportContentRes = z.infer<typeof querySurgeryReportContentResSchema>;

export const querySurgeryReportResSchema = z.object({
  id: z.string().nullable(),
  executeDoctorName: z.string().nullable(),
  executeDateTime: z.string().nullable(),
  surgeryNameEn: z.string().nullable(),
  surgeryNameZh: z.string().nullable(),
  divCode: z.string().nullable(),
  divName: z.string().nullable(),
  patientId: z.string().nullable(),
  encounterId: z.string().nullable(),
  tenantId: z.string().nullable(),
  surgeryReportId: z.string().nullable(),
  practitionerName: z.string().nullable(),
  surgeryCode: z.string().nullable(),
  reportDatetime: z.string().nullable()
});
export type QuerySurgeryReportRes = z.infer<typeof querySurgeryReportResSchema>;

export const queryUserFavFuncResSchema = z.object({
  feFuncId: z.string().nullable()
});
export type QueryUserFavFuncRes = z.infer<typeof queryUserFavFuncResSchema>;

export const queryVaccinationRecordResSchema = z.object({
  vaccineId: z.string().nullable(),
  immuDatetime: z.string().nullable(),
  immuAgencyName: z.string().nullable()
});
export type QueryVaccinationRecordRes = z.infer<typeof queryVaccinationRecordResSchema>;

export const deleteUserFavFuncReqSchema = z.object({
  feFuncIds: z.preprocess((val) => val === undefined ? null : val, z.array(z.string()).nullish())
});
export type DeleteUserFavFuncReq = z.infer<typeof deleteUserFavFuncReqSchema>;

export const apiPageResponseObjectSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: z.unknown().nullable(),
  totalPageSize: z.number().nullable(),
  totalItemSize: z.number().nullable(),
  currentPageItemSize: z.number().nullable(),
  pageSize: z.number().nullable()
});
export type ApiPageResponseObject = z.infer<typeof apiPageResponseObjectSchema>;

export const apiResponseQueryAdmissionNoteResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: queryAdmissionNoteResSchema.nullable()
});
export type ApiResponseQueryAdmissionNoteRes = z.infer<typeof apiResponseQueryAdmissionNoteResSchema>;

export const apiResponseQueryAllOutpatientDataResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: queryAllOutpatientDataResSchema.nullable()
});
export type ApiResponseQueryAllOutpatientDataRes = z.infer<typeof apiResponseQueryAllOutpatientDataResSchema>;

export const apiResponseListQueryDischargeMedicationResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: z.array(queryDischargeMedicationResSchema).nullable()
});
export type ApiResponseListQueryDischargeMedicationRes = z.infer<typeof apiResponseListQueryDischargeMedicationResSchema>;

export const apiResponseQueryDischargeNoteResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: queryDischargeNoteResSchema.nullable()
});
export type ApiResponseQueryDischargeNoteRes = z.infer<typeof apiResponseQueryDischargeNoteResSchema>;

export const apiResponseQueryEncounterResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: queryEncounterResSchema.nullable()
});
export type ApiResponseQueryEncounterRes = z.infer<typeof apiResponseQueryEncounterResSchema>;

export const apiResponseListQueryEncounterResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: z.array(queryEncounterResSchema).nullable()
});
export type ApiResponseListQueryEncounterRes = z.infer<typeof apiResponseListQueryEncounterResSchema>;

export const apiResponseListQueryInpatientEncounterOrderResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: z.array(queryInpatientEncounterOrderResSchema).nullable()
});
export type ApiResponseListQueryInpatientEncounterOrderRes = z.infer<typeof apiResponseListQueryInpatientEncounterOrderResSchema>;

export const apiResponseQueryInpatientFuncResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: queryInpatientFuncResSchema.nullable()
});
export type ApiResponseQueryInpatientFuncRes = z.infer<typeof apiResponseQueryInpatientFuncResSchema>;

export const apiResponseListQueryNursingNoteResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: z.array(queryNursingNoteResSchema).nullable()
});
export type ApiResponseListQueryNursingNoteRes = z.infer<typeof apiResponseListQueryNursingNoteResSchema>;

export const apiResponseListQueryProgressNoteResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: z.array(queryProgressNoteResSchema).nullable()
});
export type ApiResponseListQueryProgressNoteRes = z.infer<typeof apiResponseListQueryProgressNoteResSchema>;

export const apiResponseQueryTprDataResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: queryTprDataResSchema.nullable()
});
export type ApiResponseQueryTprDataRes = z.infer<typeof apiResponseQueryTprDataResSchema>;

export const apiResponseQueryLabReportByIdResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: queryLabReportByIdResSchema.nullable()
});
export type ApiResponseQueryLabReportByIdRes = z.infer<typeof apiResponseQueryLabReportByIdResSchema>;

export const apiResponseListQueryLabReportByCategoryResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: z.array(queryLabReportByCategoryResSchema).nullable()
});
export type ApiResponseListQueryLabReportByCategoryRes = z.infer<typeof apiResponseListQueryLabReportByCategoryResSchema>;

export const apiResponseQueryLabReportTrendResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: queryLabReportTrendResSchema.nullable()
});
export type ApiResponseQueryLabReportTrendRes = z.infer<typeof apiResponseQueryLabReportTrendResSchema>;

export const apiResponseListQueryLabReportResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: z.array(queryLabReportResSchema).nullable()
});
export type ApiResponseListQueryLabReportRes = z.infer<typeof apiResponseListQueryLabReportResSchema>;

export const apiResponseQueryMedicationKnowledgeResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: queryMedicationKnowledgeResSchema.nullable()
});
export type ApiResponseQueryMedicationKnowledgeRes = z.infer<typeof apiResponseQueryMedicationKnowledgeResSchema>;

export const apiResponseQueryPatientCardResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: queryPatientCardResSchema.nullable()
});
export type ApiResponseQueryPatientCardRes = z.infer<typeof apiResponseQueryPatientCardResSchema>;

export const apiResponseQueryPatientDetailMultiTenantResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: queryPatientDetailMultiTenantResSchema.nullable()
});
export type ApiResponseQueryPatientDetailMultiTenantRes = z.infer<typeof apiResponseQueryPatientDetailMultiTenantResSchema>;

export const apiResponseQueryPatientDetailResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: queryPatientDetailResSchema.nullable()
});
export type ApiResponseQueryPatientDetailRes = z.infer<typeof apiResponseQueryPatientDetailResSchema>;

export const apiResponseListQueryPatientSearchLogResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: z.array(queryPatientSearchLogResSchema).nullable()
});
export type ApiResponseListQueryPatientSearchLogRes = z.infer<typeof apiResponseListQueryPatientSearchLogResSchema>;

export const apiResponseQueryPatientObservationDataResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: queryPatientObservationDataResSchema.nullable()
});
export type ApiResponseQueryPatientObservationDataRes = z.infer<typeof apiResponseQueryPatientObservationDataResSchema>;

export const apiResponseQueryExamRoomReportByIdResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: queryExamRoomReportByIdResSchema.nullable()
});
export type ApiResponseQueryExamRoomReportByIdRes = z.infer<typeof apiResponseQueryExamRoomReportByIdResSchema>;

export const apiResponseListQueryExamReportResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: z.array(queryExamReportResSchema).nullable()
});
export type ApiResponseListQueryExamReportRes = z.infer<typeof apiResponseListQueryExamReportResSchema>;

export const apiResponseQueryImageReportByIdResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: queryImageReportByIdResSchema.nullable()
});
export type ApiResponseQueryImageReportByIdRes = z.infer<typeof apiResponseQueryImageReportByIdResSchema>;

export const apiResponseListQueryImageReportResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: z.array(queryImageReportResSchema).nullable()
});
export type ApiResponseListQueryImageReportRes = z.infer<typeof apiResponseListQueryImageReportResSchema>;

export const apiResponseQueryPacsConnectInfoResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: queryPacsConnectInfoResSchema.nullable()
});
export type ApiResponseQueryPacsConnectInfoRes = z.infer<typeof apiResponseQueryPacsConnectInfoResSchema>;

export const apiResponseQueryPathologyReportContentResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: queryPathologyReportContentResSchema.nullable()
});
export type ApiResponseQueryPathologyReportContentRes = z.infer<typeof apiResponseQueryPathologyReportContentResSchema>;

export const apiResponseListQueryPathologyReportResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: z.array(queryPathologyReportResSchema).nullable()
});
export type ApiResponseListQueryPathologyReportRes = z.infer<typeof apiResponseListQueryPathologyReportResSchema>;

export const apiResponseQuerySurgeryReportContentResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: querySurgeryReportContentResSchema.nullable()
});
export type ApiResponseQuerySurgeryReportContentRes = z.infer<typeof apiResponseQuerySurgeryReportContentResSchema>;

export const apiResponseListQuerySurgeryReportResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: z.array(querySurgeryReportResSchema).nullable()
});
export type ApiResponseListQuerySurgeryReportRes = z.infer<typeof apiResponseListQuerySurgeryReportResSchema>;

export const apiResponseListQueryUserFavFuncResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: z.array(queryUserFavFuncResSchema).nullable()
});
export type ApiResponseListQueryUserFavFuncRes = z.infer<typeof apiResponseListQueryUserFavFuncResSchema>;

export const apiResponseListQueryVaccinationRecordResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: z.array(queryVaccinationRecordResSchema).nullable()
});
export type ApiResponseListQueryVaccinationRecordRes = z.infer<typeof apiResponseListQueryVaccinationRecordResSchema>;

export const apiResponseObjectSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: z.unknown().nullable()
});
export type ApiResponseObject = z.infer<typeof apiResponseObjectSchema>;

export const reqCrudSaveSchema = z.object({
  systemNo: z.string().min(1),
  tableName: z.string().min(1),
  tableData: z.preprocess((val) => val === undefined ? null : val, z.array(z.unknown()).nullish())
});
export type ReqCrudSave = z.infer<typeof reqCrudSaveSchema>;

export const logPatientSearchReqSchema = z.object({
  patientId: z.preprocess((val) => val === "" || val === undefined ? null : val, z.string().nullish()),
  startDate: z.preprocess((val) => val === "" || val === undefined ? null : val, z.string().nullish()),
  endDate: z.preprocess((val) => val === "" || val === undefined ? null : val, z.string().nullish())
});
export type LogPatientSearchReq = z.infer<typeof logPatientSearchReqSchema>;

export const apiResponseVoidSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: z.unknown().nullable()
});
export type ApiResponseVoid = z.infer<typeof apiResponseVoidSchema>;

export const addUserFavFuncReqSchema = z.object({
  feFuncIds: z.preprocess((val) => val === undefined ? null : val, z.array(z.string()).nullish())
});
export type AddUserFavFuncReq = z.infer<typeof addUserFavFuncReqSchema>;

// Query Parameter Schemas

export const queryVaccinationRecordQuerySchema = z.object({
  mrn: z.string()
});
export type QueryVaccinationRecordQuery = z.infer<typeof queryVaccinationRecordQuerySchema>;

export const querySurgeryReportListQuerySchema = z.object({
  patientId: z.string().nullish(),
  encounterId: z.string().nullish(),
  startDatetime: z.string().nullish(),
  endDatetime: z.string().nullish()
});
export type QuerySurgeryReportListQuery = z.infer<typeof querySurgeryReportListQuerySchema>;

export const queryPathologyReportListQuerySchema = z.object({
  patientId: z.string().nullish(),
  encounterId: z.string().nullish(),
  startDatetime: z.string().nullish(),
  endDatetime: z.string().nullish()
});
export type QueryPathologyReportListQuery = z.infer<typeof queryPathologyReportListQuerySchema>;

export const queryImageReportListQuerySchema = z.object({
  patientId: z.string().nullish(),
  encounterId: z.string().nullish(),
  startDatetime: z.string().nullish(),
  endDatetime: z.string().nullish()
});
export type QueryImageReportListQuery = z.infer<typeof queryImageReportListQuerySchema>;

export const queryExamRoomReportListQuerySchema = z.object({
  patientId: z.string().nullish(),
  encounterId: z.string().nullish(),
  startDatetime: z.string().nullish(),
  endDatetime: z.string().nullish()
});
export type QueryExamRoomReportListQuery = z.infer<typeof queryExamRoomReportListQuerySchema>;

export const queryObservationDataQuerySchema = z.object({
  patientId: z.string(),
  startDatetime: z.string().nullish(),
  endDatetime: z.string().nullish()
});
export type QueryObservationDataQuery = z.infer<typeof queryObservationDataQuerySchema>;

export const queryObservationDataMultiTenantQuerySchema = z.object({
  patientId: z.string(),
  startDatetime: z.string().nullish(),
  endDatetime: z.string().nullish()
});
export type QueryObservationDataMultiTenantQuery = z.infer<typeof queryObservationDataMultiTenantQuerySchema>;

export const queryPatientDetailQuerySchema = z.object({
  mrn: z.string(),
  startDate: z.string(),
  endDate: z.string()
});
export type QueryPatientDetailQuery = z.infer<typeof queryPatientDetailQuerySchema>;

export const queryPatientDetailMultiTenantQuerySchema = z.object({
  mrn: z.string(),
  startDate: z.string(),
  endDate: z.string()
});
export type QueryPatientDetailMultiTenantQuery = z.infer<typeof queryPatientDetailMultiTenantQuerySchema>;

export const queryMedicationKnowledgeQuerySchema = z.object({
  orderCode: z.string()
});
export type QueryMedicationKnowledgeQuery = z.infer<typeof queryMedicationKnowledgeQuerySchema>;

export const queryLabReportQuerySchema = z.object({
  mrn: z.string(),
  startDatetime: z.string().nullish(),
  endDatetime: z.string().nullish()
});
export type QueryLabReportQuery = z.infer<typeof queryLabReportQuerySchema>;

export const queryLabReportTrendQuerySchema = z.object({
  mrn: z.string(),
  startDatetime: z.string().nullish(),
  endDatetime: z.string().nullish(),
  itemCodes: z.array(z.string()).nullish()
});
export type QueryLabReportTrendQuery = z.infer<typeof queryLabReportTrendQuerySchema>;

export const queryLabReportMultiTenantQuerySchema = z.object({
  patientId: z.string(),
  startDatetime: z.string().nullish(),
  endDatetime: z.string().nullish()
});
export type QueryLabReportMultiTenantQuery = z.infer<typeof queryLabReportMultiTenantQuerySchema>;

export const queryLabReportByCategoryQuerySchema = z.object({
  mrn: z.string(),
  category: z.string(),
  startDatetime: z.string().nullish(),
  endDatetime: z.string().nullish()
});
export type QueryLabReportByCategoryQuery = z.infer<typeof queryLabReportByCategoryQuerySchema>;

export const downloadFileQuerySchema = z.object({
  fileId: z.string()
});
export type DownloadFileQuery = z.infer<typeof downloadFileQuerySchema>;

export const queryTprDataQuerySchema = z.object({
  encounterId: z.string()
});
export type QueryTprDataQuery = z.infer<typeof queryTprDataQuerySchema>;

export const queryProgressNoteQuerySchema = z.object({
  encounterId: z.string()
});
export type QueryProgressNoteQuery = z.infer<typeof queryProgressNoteQuerySchema>;

export const queryNursingNoteQuerySchema = z.object({
  encounterId: z.string()
});
export type QueryNursingNoteQuery = z.infer<typeof queryNursingNoteQuerySchema>;

export const queryInpatientFuncQuerySchema = z.object({
  encounterId: z.string()
});
export type QueryInpatientFuncQuery = z.infer<typeof queryInpatientFuncQuerySchema>;

export const queryInpatientEncounterOrderQuerySchema = z.object({
  encounterId: z.string()
});
export type QueryInpatientEncounterOrderQuery = z.infer<typeof queryInpatientEncounterOrderQuerySchema>;

export const queryEncounterQuerySchema = z.object({
  mrn: z.string(),
  startDatetime: z.string().nullish(),
  endDatetime: z.string().nullish(),
  encounterTypes: z.array(z.enum(["IP", "OP", "ER", "PE"])).nullish()
});
export type QueryEncounterQuery = z.infer<typeof queryEncounterQuerySchema>;

export const queryEncounterMultiTenantQuerySchema = z.object({
  patientId: z.string(),
  startDatetime: z.string().nullish(),
  endDatetime: z.string().nullish(),
  encounterTypes: z.array(z.enum(["IP", "OP", "ER", "PE"])).nullish()
});
export type QueryEncounterMultiTenantQuery = z.infer<typeof queryEncounterMultiTenantQuerySchema>;

export const queryDischargeNoteQuerySchema = z.object({
  encounterId: z.string()
});
export type QueryDischargeNoteQuery = z.infer<typeof queryDischargeNoteQuerySchema>;

export const queryDischargeMedicationQuerySchema = z.object({
  encounterId: z.string()
});
export type QueryDischargeMedicationQuery = z.infer<typeof queryDischargeMedicationQuerySchema>;

export const queryAllOutpatientDataQuerySchema = z.object({
  mrn: z.string(),
  encounterId: z.string()
});
export type QueryAllOutpatientDataQuery = z.infer<typeof queryAllOutpatientDataQuerySchema>;

export const queryAdmissionNoteQuerySchema = z.object({
  encounterId: z.string()
});
export type QueryAdmissionNoteQuery = z.infer<typeof queryAdmissionNoteQuerySchema>;

export const queryQuerySchema = z.object({
  system: z.string(),
  tableName: z.string(),
  pageNum: z.number(),
  pageSize: z.number(),
  filters: z.string().nullish(),
  likes: z.string().nullish(),
  sorts: z.string().nullish()
});
export type QueryQuery = z.infer<typeof queryQuerySchema>;

export const queryStructureQuerySchema = z.object({
  system: z.string(),
  tableName: z.string()
});
export type QueryStructureQuery = z.infer<typeof queryStructureQuerySchema>;
